import React, { useEffect, useState } from 'react';
import { DatabaseIcon, PlusCircleIcon, CheckIcon, GaugeIcon } from 'lucide-react';
interface Tank {
  id: number | string;
  vorher: number;
  nachher: number;
  amount: number | null;
  maxCapacity: number;
  bemerkung: string;
  chargenNr?: string;
}
export function TankStatus() {
  const initialAnsatzTanks = [{
    id: 'AT1',
    vorher: 15.2,
    nachher: 15.8,
    amount: null,
    maxCapacity: 25,
    bemerkung: ''
  }, {
    id: 'AT2',
    vorher: 12.4,
    nachher: 12.9,
    amount: null,
    maxCapacity: 25,
    bemerkung: ''
  }];
  const initialZusatzTanks = [{
    id: 'Entschäumer',
    vorher: 10.2,
    nachher: 10.5,
    amount: null,
    maxCapacity: 15,
    bemerkung: '',
    chargenNr: 'CH-2025-042'
  }, {
    id: 'Dispergier',
    vorher: 15.5,
    nachher: 16.0,
    amount: null,
    maxCapacity: 20,
    bemerkung: '',
    chargenNr: 'CH-2025-043'
  }, {
    id: 'IBC',
    vorher: 0,
    nachher: 0,
    amount: null,
    maxCapacity: 1000,
    bemerkung: '',
    chargenNr: 'CH-2025-044'
  }];
  const initialTanks = [{
    id: 1,
    vorher: 33,
    nachher: 33.5,
    amount: 27.7,
    maxCapacity: 50,
    bemerkung: ''
  }, {
    id: 2,
    vorher: 39.6,
    nachher: 42.4,
    amount: 2.8,
    maxCapacity: 55,
    bemerkung: ''
  }, {
    id: 3,
    vorher: 43.1,
    nachher: 43.1,
    amount: 0,
    maxCapacity: 60,
    bemerkung: ''
  }, {
    id: 4,
    vorher: 33,
    nachher: 3.9,
    amount: 0,
    maxCapacity: 45,
    bemerkung: ''
  }, {
    id: 5,
    vorher: 43.1,
    nachher: 12.5,
    amount: 13.4,
    maxCapacity: 65,
    bemerkung: ''
  }, {
    id: 6,
    vorher: 41.7,
    nachher: 30.5,
    amount: 12.2,
    maxCapacity: 70,
    bemerkung: ''
  }, {
    id: 7,
    vorher: 10.5,
    nachher: 10.3,
    amount: null,
    maxCapacity: 40,
    bemerkung: ''
  }];
  const [ansatzTanks, setAnsatzTanks] = useState<Tank[]>(initialAnsatzTanks);
  const [zusatzTanks, setZusatzTanks] = useState<Tank[]>(initialZusatzTanks);
  const [tanks, setTanks] = useState<Tank[]>(initialTanks);
  const [editingBemerkung, setEditingBemerkung] = useState<number | string | null>(null);
  const [editingChargenNr, setEditingChargenNr] = useState<string | null>(null);
  const [editStates, setEditStates] = useState({
    bemerkungen: {} as Record<string | number, string>,
    chargenNummern: {} as Record<string, string>,
    isEditing: {} as Record<string | number, boolean>
  });
  const handleStartEdit = (tankId: string | number, type: 'bemerkung' | 'chargenNr', initialValue: string = '') => {
    setEditStates(prev => ({
      ...prev,
      [type === 'bemerkung' ? 'bemerkungen' : 'chargenNummern']: {
        ...prev[type === 'bemerkung' ? 'bemerkungen' : 'chargenNummern'],
        [tankId]: initialValue
      },
      isEditing: {
        ...prev.isEditing,
        [tankId]: true
      }
    }));
    if (type === 'bemerkung') {
      setEditingBemerkung(tankId);
    } else {
      setEditingChargenNr(tankId as string);
    }
  };
  const handleSaveBemerkung = (tankId: number | string) => {
    const newBemerkung = editStates.bemerkungen[tankId] || '';
    if (typeof tankId === 'string') {
      setZusatzTanks(zusatzTanks.map(tank => tank.id === tankId ? {
        ...tank,
        bemerkung: newBemerkung
      } : tank));
    } else {
      setTanks(tanks.map(tank => tank.id === tankId ? {
        ...tank,
        bemerkung: newBemerkung
      } : tank));
    }
    setEditStates(prev => ({
      ...prev,
      bemerkungen: {
        ...prev.bemerkungen,
        [tankId]: ''
      },
      isEditing: {
        ...prev.isEditing,
        [tankId]: false
      }
    }));
    setEditingBemerkung(null);
  };
  const handleSaveChargenNr = (tankId: string) => {
    const newChargenNr = editStates.chargenNummern[tankId] || '';
    setZusatzTanks(zusatzTanks.map(tank => tank.id === tankId ? {
      ...tank,
      chargenNr: newChargenNr
    } : tank));
    setEditStates(prev => ({
      ...prev,
      chargenNummern: {
        ...prev.chargenNummern,
        [tankId]: ''
      },
      isEditing: {
        ...prev.isEditing,
        [tankId]: false
      }
    }));
    setEditingChargenNr(null);
  };
  useEffect(() => {
    const interval = setInterval(() => {
      setAnsatzTanks(currentTanks => currentTanks.map(tank => {
        if (editingBemerkung === tank.id || editingChargenNr === tank.id) {
          return tank;
        }
        const change = Math.random() * 0.5 - 0.2;
        const newValue = Math.max(0, Math.min(tank.maxCapacity, tank.nachher + change));
        return {
          ...tank,
          nachher: Number(newValue.toFixed(1))
        };
      }));
      setZusatzTanks(currentTanks => currentTanks.map(tank => {
        if (editingBemerkung === tank.id || editingChargenNr === tank.id) {
          return tank;
        }
        const change = Math.random() * 0.3 - 0.1;
        const newValue = Math.max(0, Math.min(tank.maxCapacity, tank.nachher + change));
        return {
          ...tank,
          nachher: Number(newValue.toFixed(1)),
          bemerkung: tank.bemerkung,
          chargenNr: tank.chargenNr
        };
      }));
      setTanks(currentTanks => currentTanks.map(tank => {
        if (editingBemerkung === tank.id || editingChargenNr === tank.id) {
          return tank;
        }
        if (!tank.amount) return tank;
        const change = Math.random() * 0.5 - 0.2;
        const newValue = Math.max(0, Math.min(tank.maxCapacity, tank.nachher + change));
        return {
          ...tank,
          nachher: Number(newValue.toFixed(1)),
          bemerkung: tank.bemerkung
        };
      }));
    }, 7000);
    return () => clearInterval(interval);
  }, [editingBemerkung, editingChargenNr]);
  const calculateFillPercentage = (current: number, max: number) => {
    return current / max * 100;
  };
  const TankCard = ({
    tank
  }: {
    tank: Tank;
  }) => {
    const actualDiff = tank.nachher - tank.vorher;
    const calculatedDiff = tank.amount || 0;
    return <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-1161">
        <div className="flex items-center justify-between mb-4" data-id="element-1162">
          <div className="space-y-1" data-id="element-1163">
            <div className="flex items-center gap-2" data-id="element-1164">
              <h4 className="font-medium text-gray-800" data-id="element-1165">
                {typeof tank.id === 'string' && !tank.id.startsWith('AT') ? tank.id : `Tank ${tank.id}`}
              </h4>
              {tank.chargenNr && typeof tank.id === 'string' && !tank.id.startsWith('AT') ? <span className="text-gray-400" data-id="element-1166">|</span> : null}
              {tank.chargenNr && typeof tank.id === 'string' && !tank.id.startsWith('AT') ? <div className="flex items-center" data-id="element-1167">
                  {editingChargenNr === tank.id ? <div className="flex gap-1" data-id="element-1168">
                      <input type="text" value={editStates.chargenNummern[tank.id] || ''} onChange={e => setEditStates(prev => ({
                  ...prev,
                  chargenNummern: {
                    ...prev.chargenNummern,
                    [tank.id]: e.target.value
                  }
                }))} placeholder="Chargen-Nr. eingeben..." className="text-xs px-2 py-0.5 border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 w-28" onKeyDown={e => {
                  if (e.key === 'Enter') handleSaveChargenNr(tank.id as string);
                }} data-id="element-1169" />
                      <button onClick={() => handleSaveChargenNr(tank.id as string)} className="px-1 py-0.5 text-gray-400 hover:text-gray-600" data-id="element-1170">
                        <CheckIcon size={12} data-id="element-1171" />
                      </button>
                    </div> : <button onClick={() => handleStartEdit(tank.id, 'chargenNr', tank.chargenNr)} className="text-xs text-[#3498dd] bg-blue-50 px-2 py-0.5 rounded hover:bg-blue-100 transition-colors" data-id="element-1172">
                      {tank.chargenNr}
                    </button>}
                </div> : null}
            </div>
          </div>
          <span className="text-xs text-gray-400" data-id="element-1173">
            Max: {tank.maxCapacity}t
          </span>
        </div>
        <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-1174">
          <div className="space-y-1" data-id="element-1175">
            <div className="text-xs text-gray-400" data-id="element-1176">Aktuelle Differenzen</div>
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-100" data-id="element-1177">
              <div className="flex justify-between items-center mb-2" data-id="element-1178">
                <span className="text-xs text-gray-600" data-id="element-1179">Ist-Diff.:</span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${actualDiff > 0 ? 'bg-green-100 text-green-700' : actualDiff < 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`} data-id="element-1180">
                  {(actualDiff > 0 ? '+' : '') + actualDiff.toFixed(1)}t
                </span>
              </div>
              <div className="flex justify-between items-center" data-id="element-1181">
                <span className="text-xs text-gray-600" data-id="element-1182">Errech. Diff.:</span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${calculatedDiff > 0 ? 'bg-green-100 text-green-700' : calculatedDiff < 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`} data-id="element-1183">
                  {(calculatedDiff > 0 ? '+' : '') + calculatedDiff.toFixed(1)}t
                </span>
              </div>
            </div>
          </div>
          <div className="space-y-1" data-id="element-1184">
            <div className="text-xs text-gray-400" data-id="element-1185">Tankinhalt</div>
            <div className="relative h-[72px] bg-gray-50 rounded-lg border border-gray-200" data-id="element-1186">
              <div className="absolute bottom-0 left-0 right-0 bg-blue-50 transition-all duration-300 rounded-lg" style={{
              height: `${calculateFillPercentage(tank.nachher, tank.maxCapacity)}%`
            }} data-id="element-1187" />
              <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-1188">
                <span className="text-lg font-semibold text-gray-700" data-id="element-1189">
                  {tank.nachher.toFixed(1)}t
                </span>
                <span className="text-xs text-gray-400" data-id="element-1190">
                  Vorher: {tank.vorher.toFixed(1)}t
                </span>
              </div>
            </div>
          </div>
        </div>
        {tank.amount !== null && <div className="px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 mb-3" data-id="element-1191">
            <div className="flex justify-between items-center" data-id="element-1192">
              <span className="text-xs text-gray-500" data-id="element-1193">Zufluss:</span>
              <span className="text-xs text-blue-600" data-id="element-1194">
                AT1 ({tank.amount}t)
              </span>
            </div>
          </div>}
        <div data-id="element-1195">
          {editingBemerkung === tank.id ? <div className="flex gap-2" data-id="element-1196">
              <input type="text" value={editStates.bemerkungen[tank.id] || ''} onChange={e => setEditStates(prev => ({
            ...prev,
            bemerkungen: {
              ...prev.bemerkungen,
              [tank.id]: e.target.value
            }
          }))} placeholder="Bemerkung eingeben..." className="flex-1 px-3 py-1.5 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500" onKeyDown={e => {
            if (e.key === 'Enter') handleSaveBemerkung(tank.id);
          }} data-id="element-1197" />
              <button onClick={() => handleSaveBemerkung(tank.id)} className="px-2 py-1.5 text-gray-400 hover:text-gray-600" data-id="element-1198">
                <CheckIcon size={14} data-id="element-1199" />
              </button>
            </div> : tank.bemerkung ? <div className="flex items-center justify-between group" data-id="element-1200">
              <span className="text-xs text-gray-600" data-id="element-1201">{tank.bemerkung}</span>
              <button onClick={() => handleStartEdit(tank.id, 'bemerkung', tank.bemerkung)} className="opacity-0 group-hover:opacity-100 transition-opacity px-2 py-1 text-gray-400 hover:text-gray-600" data-id="element-1202">
                <PlusCircleIcon size={14} data-id="element-1203" />
              </button>
            </div> : <button onClick={() => handleStartEdit(tank.id, 'bemerkung')} className="w-full py-1.5 px-3 text-xs text-gray-400 hover:text-gray-600 border border-dashed border-gray-200 rounded flex items-center justify-center gap-1.5" data-id="element-1204">
              <PlusCircleIcon size={14} data-id="element-1205" />
              Bemerkung
            </button>}
        </div>
      </div>;
  };
  return <div className="bg-gray-50 rounded-lg p-6" data-id="element-1206">
      <div className="flex items-center gap-2 mb-6" data-id="element-1207">
        <DatabaseIcon className="text-[#3498dd]" size={20} data-id="element-1208" />
        <h3 className="text-lg font-semibold text-gray-800" data-id="element-1209">Tankbestände</h3>
      </div>
      <div className="space-y-8" data-id="element-1210">
        <div className="grid grid-cols-2 gap-4" data-id="element-1211">
          <div data-id="element-1212">
            <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-1213">
              Ansatztanks
            </h4>
            <div className="grid grid-cols-1 gap-4" data-id="element-1214">
              <div className="flex-1" data-id="element-1215">
                <TankCard tank={ansatzTanks[0]} data-id="element-1216" />
              </div>
              <div className="flex-1" data-id="element-1217">
                <TankCard tank={ansatzTanks[1]} data-id="element-1218" />
              </div>
            </div>
          </div>
          <div data-id="element-1219">
            <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-1220">
              Zusatzstoffe
            </h4>
            <div className="grid grid-cols-2 gap-4" data-id="element-1221">
              <div className="flex-1" data-id="element-1222">
                <TankCard tank={zusatzTanks[0]} data-id="element-1223" />
              </div>
              <div className="flex-1" data-id="element-1224">
                <TankCard tank={zusatzTanks[1]} data-id="element-1225" />
              </div>
              <div className="col-span-2" data-id="element-1226">
                <TankCard tank={zusatzTanks[2]} data-id="element-1227" />
              </div>
            </div>
          </div>
        </div>
        <div data-id="element-1228">
          <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-1229">Lagertanks</h4>
          <div className="grid grid-cols-4 gap-4" data-id="element-1230">
            {tanks.map(tank => <TankCard key={tank.id} tank={tank} data-id="element-1231" />)}
          </div>
        </div>
      </div>
    </div>;
}